/* globals jQuery */

/**
 * External dependencies
 */
import lottie from 'lottie-web';
import CliboardJS from 'clipboard';

/**
 * Internal dependencies
 */
import setupFocusTrap from './theme/focus-trap';
import determineTarget from './theme/determine-target';
import toggleElement from './theme/toggle-element';
import setupModals from './theme/modals';
import setupShareButtons from './theme/share-button';
import setupDropdowns from './theme/dropdown';

jQuery( $ => {
	const $window = $( window );
	const $body = $( 'body' );

	// =========================
	// ! External Link Mandate
	// =========================

	document.querySelectorAll( ':where(.page-content, .js-modal) a[href^="http"]' ).forEach( anchor => {
		if ( ! anchor.target && ( ! anchor.href.startsWith( window.location.origin ) || anchor.href.match( /\.pdf$/ ) ) ) {
			anchor.setAttribute( 'target', '_blank' );
			anchor.setAttribute( 'rel', 'noreferrer noopener' );
		}
	} );

	// =========================
	// ! Basic Toggles
	// =========================

	$body.on( 'click', '.js-toggle', function( event ) {
		const target = determineTarget( this );
		if ( target ) {
			event.preventDefault();
			const isOpen = toggleElement( target, null, 'focus' );

			// For global styling that might be needed
			$body.toggleClass( `show-${ target.id }`, isOpen );
		}
	} );

	$body.on( 'click', '.js-close', function() {
		toggleElement( this.closest( '[aria-hidden="false"]' ), false );
	} );

	$body.on( 'click', '[data-close]', function() {
		toggleElement( this.dataset.close, false );
	} );

	$body.on( 'click', '.interval-timeline-nav a[href^="#"]', function() {
		toggleElement( 'navigation', false );
	} );

	$body.on( 'toggle-off', '.js-modal', function() {
		this.querySelectorAll( 'iframe' ).forEach( iframe => {
			const replacement = iframe.cloneNode();
			iframe.parentElement.insertBefore( replacement, iframe );
			iframe.remove();
		} );
	} );

	// =========================
	// ! Modals
	// =========================

	// Add classes to modals automatically
	$( '.interval-timeline-modal' ).addClass( 'js-modal' );
	$( '.interval-timeline-modal__close' ).addClass( 'js-modal-close' );
	$( '.interval-timeline-modal__frame' ).addClass( 'js-modal-backdrop' );

	setupModals();

	// =========================
	// ! Hidden Target Handling
	// =========================

	function handleHiddenTarget( event ) {
		// Get the target element
		const target = document.getElementById( window.location.hash.substring( 1 ) );
		if ( ! target ) {
			return;
		}

		// See if it's within a hidden container
		const container = target.closest( '[id][aria-hidden="true"]' );
		if ( ! container ) {
			return;
		}

		// Find the control for the container
		const control = document.querySelector( `[aria-controls=${ container.id }]` );
		if ( ! control ) {
			return;
		}

		// Prevent automatic scolling
		if ( event ) {
			event.preventDefault();
		}

		// Open the container and focus
		control.click();
		target.scrollIntoView();
	}

	window.addEventListener( 'hashchange', handleHiddenTarget );
	handleHiddenTarget();

	// =========================
	// ! Dropdown
	// =========================

	setupDropdowns();

	// =========================
	// ! Bookmark/Share Links
	// =========================

	setupShareButtons();

	$body.on( 'click', 'a.js-clipboard', e => e.preventDefault() );

	const hrefClipboard = new CliboardJS( 'a.js-clipboard', {
		text( trigger ) {
			return trigger.getAttribute( 'href' );
		},
	} );

	const clipboardTooltipTimeouts = new WeakMap();
	hrefClipboard.on( 'success', e => {
		clearTimeout( clipboardTooltipTimeouts.get( e.trigger ) );
		$( e.trigger ).text( 'Link copied!' );
		const timeout = setTimeout( () => $( e.trigger ).text( 'Copy Link' ), 5000 );
		clipboardTooltipTimeouts.set( e.trigger, timeout );
	} );

	// =========================
	// ! Masonry Grid
	// =========================

	function updateMasonryGrid( grid, force ) {
		grid.querySelectorAll( '.interval-timeline-grid__cell' ).forEach( cell => {
			if ( ! cell.classList.contains( 'js-masonry-cell' ) || force ) {
				cell.classList.add( 'js-masonry-cell' );
				cell.style.setProperty( '--masonry-size', Math.ceil( cell.firstElementChild.offsetHeight ) );
			}
		} );
	}

	document.querySelectorAll( '.interval-timeline-grid' ).forEach( grid => {
		grid.classList.add( 'js-masonry-grid' );
		updateMasonryGrid( grid, true );
	} );

	// =========================
	// ! Peek-a-boo Nav Handling
	// =========================

	let lastScroll = 0;
	let lastDirection = null;
	let lastPastHeader = null;
	const headerThreshold = () => 170;

	$window.scroll( function() {
		const scroll = $window.scrollTop();
		const direction = scroll >= lastScroll ? 1 : -1;
		const pastHeader = scroll > headerThreshold();

		if ( direction !== lastDirection || pastHeader !== lastPastHeader ) {
			$body.toggleClass( 'animating-sticky-nav--in', direction < 0 && pastHeader );
			$body.toggleClass( 'animating-sticky-nav--out', direction > 0 && pastHeader );
		}

		lastScroll = scroll;
		lastDirection = direction;
		lastPastHeader = pastHeader;
	} );

	// =========================
	// ! Sticky Nav Tracking
	// =========================

	if ( typeof IntersectionObserver !== 'undefined' ) {
		// =========================
		// ! - Page Nav Scroll Spy
		// =========================

		const $navlinks = $( '.stickynav a' );

		const headingObserver = new IntersectionObserver( changes => {
			const matches = [];

			changes.forEach( change => {
				const { id } = change.target;
				if ( change.isIntersecting ) {
					matches.push( id );
				}
			} );

			// Update with the first match
			if ( matches.length > 0 ) {
				$navlinks.removeClass( 'is-active' );
				$navlinks.filter( `[href="#${ matches[ matches.length - 1 ] }"]` ).addClass( 'is-active' );
			}
		}, {
			rootMargin: '-50px 0% -20% 0%',
		} );

		$( '.page-content' ).find( '[id^="decade-"]' ).each( function() {
			headingObserver.observe( this );
		} );
	}

	// =========================
	// ! Image Fullscreen Support
	// =========================

	let takeoverCount = 1;

	function setupImageTakeovers( container ) {
		container.querySelectorAll( '.wp-block-gallery .wp-block-image, .wp-block-image.expandable' ).forEach( element => {
			const img = element.querySelector( 'img' );
			const takeoverId = `takeover-${ takeoverCount++ }`;

			const takeover = document.createElement( 'figure' );
			takeover.id = takeoverId;
			takeover.classList.add( 'interval-timeline-takeover' );
			takeover.classList.add( 'js-modal' );

			const expand = document.createElement( 'button' );
			expand.type = 'button';
			expand.classList.add( 'interval-timeline-toggle' );
			expand.setAttribute( 'aria-controls', takeoverId );
			expand.innerText = 'View Full Size';

			const collapse = document.createElement( 'button' );
			collapse.type = 'button';
			collapse.classList.add( 'interval-timeline-toggle' );
			collapse.classList.add( 'js-modal-close' );
			collapse.innerText = 'Close';

			element.append( expand );
			takeover.append( collapse );
			takeover.append( img.cloneNode() );
			document.body.append( takeover );
		} );
	}

	setupImageTakeovers( document );

	// =========================
	// ! Modals
	// =========================

	if ( window.location.hash.match( /#!(\d+)/ ) ) {
		const [ , id ] = window.location.hash.match( /#!(\d+)/ );
		const modal = document.getElementById( 'interval_timeline_modal_template' )?.content.firstElementChild.cloneNode( true );

		if ( id && modal ) {
			document.body.appendChild( modal );
			setupFocusTrap( modal );

			const content = modal.querySelector( '.js-modal-content' );
			fetch( `/wp-json/interval-timeline/modal/${ id }` )
				.then( response => {
					if ( ! response.ok ) {
						throw new Error( 'Article not found' );
					}

					return response.json();
				} )
				.then( response => {
					modal.dataset.url = response.url;
					content.innerHTML = response.content;
					toggleElement( modal, true, 'focus' );
				} )
				.catch( error => {
					content.innerText = error;
				} );
		}
	}

	$( '.js-modal.auto-open' ).each( function() {
		toggleElement( this, true, 'focus' );

		// Replace URL if applicable
		if ( this.dataset.url ) {
			history.replaceState( {}, '', window.location.pathname + '#!' + this.dataset.url );
		}
	} );

	// =========================
	// ! Audio
	// =========================

	$( '.interval-timeline-audio' ).each( function() {
		const audio = $( this ).find( 'audio' )[ 0 ];
		const buttonPlayPause = document.createElement( 'button' );
		const buttonSound = document.createElement( 'button' );

		buttonPlayPause.setAttribute( 'class', 'interval-timeline-audio__play' );
		buttonPlayPause.innerText = 'Play or Pause';
		buttonSound.setAttribute( 'class', 'interval-timeline-audio__sound-on' );
		buttonSound.innerText = 'Sound On or Sound Off';

		this.append( buttonPlayPause );
		this.append( buttonSound );

		audio.style.display = 'none';

		function playPause() {
			if ( audio.paused ) {
				audio.play();
				buttonPlayPause.className = 'interval-timeline-audio__pause';
			} else {
				audio.pause();
				buttonPlayPause.className = 'interval-timeline-audio__play';
			}
		}
		function mute() {
			if ( audio.muted ) {
				audio.muted = false;
				buttonSound.className = 'interval-timeline-audio__sound-on';
			} else {
				audio.muted = true;
				buttonSound.className = 'interval-timeline-audio__sound-off';
			}
		}

		buttonPlayPause.addEventListener( 'click', playPause );
		buttonSound.addEventListener( 'click', mute );

		audio.addEventListener( 'ended', function() {
			buttonPlayPause.className = 'interval-timeline-audio__play';
		} );
	} );

	// =========================
	// ! Intersection Observers
	// =========================

	if ( typeof IntersectionObserver !== 'undefined' ) {
		// =========================
		// ! - Sticky Header Tracking
		// =========================

		const splash = document.getElementById( 'splash' );
		const content = document.getElementById( 'content' );
		if ( splash && content ) {
			const splashObserver = new IntersectionObserver( ( [ change ] ) => {
				$body.toggleClass( 'past-splash', change.isIntersecting );
			}, {
				rootMargin: '0% 0% -99%',
			} );

			$body.addClass( 'has-splash' );
			splashObserver.observe( content );
		}

		// =========================
		// ! - In-view Identifier
		// =========================

		const inviewObserver = new IntersectionObserver( changes => {
			changes.forEach( change => {
				if ( change.intersectionRatio > 0 ) {
					$( change.target ).addClass( 'in-view' );
				}
			} );
		} );

		$( '.interval-timeline-section, .wp-block-media-text > *, .interval-timeline-wrapper > h2, .interval-timeline-wrapper > p, .interval-timeline-grid__cell' ).each( function() {
			$( this ).addClass( 'observing' );
			inviewObserver.observe( this );
		} );
	}

	// =========================
	// ! Lottie Animations
	// =========================

	const lottieAnimations = new WeakMap();

	const lottieObserver = new IntersectionObserver( changes => {
		changes.forEach( change => {
			if ( change.intersectionRatio > 0 ) {
				const animation = lottieAnimations.get( change.target );
				if ( animation ) {
					lottieAnimations.delete( change.target );
					animation.play();
				}
			}
		} );
	}, { margin: '0 0 50%' } );

	document.querySelectorAll( '.js-animation' ).forEach( element => {
		const config = {
			container: element,
			renderer: 'svg',
			loop: false,
			autoplay: false,
		};

		if ( element.dataset.animationFile ) {
			config.path = element.dataset.animationFile;
		} else
		if ( element.dataset.animationData ) {
			config.animationData = JSON.parse( element.dataset.animationData );
		}

		const animation = lottie.loadAnimation( config );
		animation.lastFrame = 0;
		animation.addEventListener( 'DOMLoaded', () => {
			element.style.setProperty( '--width', `${ animation.animationData.w }px` );
		} );

		lottieObserver.observe( element );
		lottieAnimations.set( element, animation );
	} );

	// =========================
	// ! Archive Handling
	// =========================

	document.querySelectorAll( '.js-archive' ).forEach( archive => {
		const premier = archive.querySelector( '.js-archive-premier' );
		const grid = archive.querySelector( '.js-archive-grid' );
		const decade = parseInt( archive.dataset.decade || 0 );
		let category = '';
		let offset = 0;

		function fetchItems( append ) {
			const params = new URLSearchParams( {
				decade,
				category,
				offset,
			} );

			archive.classList.add( 'loading' );
			if ( ! append && premier ) {
				premier.remove();
			}

			fetch( `/wp-json/interval-timeline/entries?${ params.toString() }` )
				.then( response => {
					if ( ! response.ok ) {
						throw new Error( `More Posts Status: ${ response.status }` );
					}

					return response.json();
				} )
				.then( ( { posts = [], more: hasMore = false } ) => {
					if ( ! append ) {
						grid.innerHTML = '';
					}

					posts.forEach( card => {
						const cell = document.createElement( 'div' );
						cell.className = 'interval-timeline-grid__cell';

						cell.innerHTML = card;

						const modal = cell.querySelector( '.js-modal' );
						if ( modal ) {
							setupFocusTrap( modal );
							setupImageTakeovers( modal );
						}

						grid.appendChild( cell );
					} );

					updateMasonryGrid( grid, false );

					if ( hasMore ) {
						more.setAttribute( 'aria-hidden', false );
						more.classList.remove( 'hidden' );
					} else {
						more.setAttribute( 'aria-hidden', true );
						more.classList.add( 'hidden' );
					}
				} )
				.catch( () => {
					// do nothing
				} )
				.finally( () => {
					archive.classList.remove( 'loading' );
				} );
		}

		const more = archive.querySelector( '.js-archive-more' );
		if ( more ) {
			more.addEventListener( 'click', event => {
				event.preventDefault();
				offset = archive.querySelectorAll( '.js-archive-item' ).length;
				fetchItems( 'append' );
			} );
		}

		const filter = archive.querySelector( '.js-archive-filter' );
		filter.addEventListener( 'change', () => {
			archive.querySelectorAll( '.js-archive-filter-button' ).forEach( button => {
				button.classList.toggle( 'is-active', button.value === filter.value );
			} );

			category = filter.value;
			offset = 0;
			fetchItems();
		} );

		archive.addEventListener( 'click', event => {
			if ( event.target.classList.contains( 'js-archive-filter-button' ) ) {
				filter.value = event.target.value;
				filter.dispatchEvent( new Event( 'change' ) );
			}
		} );
	} );

	// =========================
	// ! Search Flyout
	// =========================

	$( '.js-search' ).each( function() {
		const searchUrl = this.dataset.searchUrl;
		const $container = $( this );
		const $results = $container.find( '.js-search-results' );

		if ( $results.length === 0 ) {
			return;
		}

		const $form = $container.find( '.js-search-form' );
		const $input = $container.find( '.js-search-input' );

		$form.on( 'submit', function( e ) {
			e.preventDefault();
			$container.addClass( 'loading' );

			$.ajax( {
				url: searchUrl,
				data: { query: $input.val() },
				success( { results } ) {
					$container.addClass( 'has-results' );
					$results.empty().append( results );

					$results.children().each( function() {
						const modal = this.querySelector( '.js-modal' );
						if ( modal ) {
							setupFocusTrap( modal );
							setupImageTakeovers( modal );
						}
					} );
				},
				error() {
					$results.empty();
				},
				complete() {
					$container.removeClass( 'loading' );
				},
			} );
		} );
	} );
} );
